import React, { useState, useEffect } from 'react';
import { Clock, Smartphone, MapPin, Zap, Users, Shield } from 'lucide-react';

const ChamaOMotoLanding = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-400 rounded-full animate-pulse"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-green-400 rounded-full animate-bounce"></div>
          <div className="absolute bottom-20 left-32 w-12 h-12 bg-pink-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-32 right-16 w-24 h-24 bg-cyan-400 rounded-full animate-bounce"></div>
        </div>

        {/* Header */}
        <header className="relative z-10 p-6">
          <nav className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                <Zap className="w-6 h-6 text-black" />
              </div>
              <span className="text-2xl font-bold">Chama o Motô</span>
            </div>
            <div className="hidden md:flex space-x-6">
              <a href="#about" className="hover:text-yellow-400 transition-colors">Sobre</a>
              <a href="#features" className="hover:text-yellow-400 transition-colors">Features</a>
              <a href="#contact" className="hover:text-yellow-400 transition-colors">Contato</a>
            </div>
          </nav>
        </header>

        {/* Hero Section */}
        <main className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 text-center">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-gradient-to-r from-yellow-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              Chama o Motô
            </h1>

            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
              O futuro da mobilidade urbana está chegando!
              <br />
              <span className="text-yellow-400 font-semibold">Conectando você ao seu destino de forma rápida, segura e econômica.</span>
            </p>

            <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-8 mb-12 border border-white/10">
              <div className="flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-yellow-400 mr-3" />
                <span className="text-2xl font-bold">Em Desenvolvimento</span>
              </div>
              <p className="text-lg opacity-90">
                Nossa equipe está trabalhando intensamente para trazer a melhor experiência de mobilidade para você.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <button className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300">
                Notifique-me do Lançamento
              </button>
              <button className="border-2 border-white/30 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/10 hover:border-white/50 transition-all duration-300">
                Saiba Mais
              </button>
            </div>
          </div>
        </main>

        {/* Features Section */}
        <section id="features" className="relative z-10 py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              O Que Esperar
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-cyan-400 rounded-2xl flex items-center justify-center mb-6">
                  <Smartphone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Interface Intuitiva</h3>
                <p className="text-lg opacity-90">
                  Design moderno e fácil de usar, pensado para proporcionar a melhor experiência do usuário.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mb-6">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Localização Precisa</h3>
                <p className="text-lg opacity-90">
                  Sistema avançado de GPS para encontrar você e seu destino com máxima precisão.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mb-6">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Segurança Total</h3>
                <p className="text-lg opacity-90">
                  Motoristas verificados, rastreamento em tempo real e suporte 24/7 para sua tranquilidade.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="relative z-10 py-20 px-6 bg-black/20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-pink-400 to-yellow-400 bg-clip-text text-transparent">
              Nossa Missão
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed mb-8 opacity-90">
              Revolucionar o transporte urbano através da tecnologia, conectando pessoas e destinos de forma eficiente,
              segura e sustentável. O <span className="text-yellow-400 font-semibold">Chama o Motô</span> nasceu da necessidade
              de criar soluções de mobilidade que realmente atendam às demandas do dia a dia brasileiro.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mt-16">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Comunidade</h3>
                <p className="text-lg opacity-90">
                  Construindo uma rede de confiança entre motoristas e passageiros.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-teal-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">Inovação</h3>
                <p className="text-lg opacity-90">
                  Tecnologia de ponta para uma experiência única de mobilidade.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="relative z-10 py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">
              Seja o Primeiro a Saber!
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Cadastre-se para receber atualizações exclusivas sobre o desenvolvimento e lançamento do app.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                    type="email"
                    placeholder="Digite seu e-mail"
                    className="flex-1 px-6 py-4 rounded-full bg-white/20 border border-white/30 placeholder-white/70 text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
                <button className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-4 rounded-full font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300">
                  Cadastrar
                </button>
              </div>
              <p className="text-sm opacity-70 mt-4">
                Prometemos não enviar spam. Apenas as novidades mais importantes!
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="relative z-10 py-12 px-6 bg-black/30 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-2 mb-6 md:mb-0">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Zap className="w-6 h-6 text-black" />
                </div>
                <span className="text-2xl font-bold">Chama o Motô</span>
              </div>

              <div className="flex space-x-6">
                <a href="#" className="hover:text-yellow-400 transition-colors">Privacidade</a>
                <a href="#" className="hover:text-yellow-400 transition-colors">Termos</a>
                <a href="#contact" className="hover:text-yellow-400 transition-colors">Contato</a>
              </div>
            </div>

            <div className="text-center mt-8 pt-8 border-t border-white/10 opacity-70">
              <p>&copy; 2025 Chama o Motô. Todos os direitos reservados.</p>
              <p className="mt-2">Em breve nas lojas de aplicativos!</p>
            </div>
          </div>
        </footer>
      </div>
  );
};

export default ChamaOMotoLanding;